import { FunctionComponent, useRef } from 'react';
import { DraftBendingTextConfiguration } from '@/types';
import { round } from '@/utils/math/round';
import { MaxSizeHint } from '../../MaxSizeHint';
import { SnapLines } from '../../SnapLines';
import { useConfigurationTransform } from '../../hooks/useConfigurationTransform';
import { useMoveOrEdit } from '../../hooks/useMoveOrEdit';
import { RendererProps } from '../ConfigurationRenderer';
import { ConfigurationHandlesWithZoom } from '../zoomConfigurationTest/ConfigurationHandlesWithZoom';
import { useBendingText } from './useBendingText';

export const BendingTextRenderer: FunctionComponent<
  RendererProps<DraftBendingTextConfiguration>
> = ({ configuration, viewMap, hasErrors, isSelected, onShowEdit }) => {
  const textElementRef = useRef<SVGTextElement>(null);

  // minimum scale for bending text must only have two digits because the designer api will create a design with this value as minimum scale and minimum scales are of type int in our system
  const minimumScale = round(
    configuration.text.style.font.minimalSize / configuration.text.style.fontSize,
    2
  );

  const {
    scale,
    maxScale,
    offset,
    rotation,
    infoText,
    isSnapTarget,
    onDelete,
    onMove,
    onPinch,
    onRotate,
    onScale,
    onSetSnapLineActive,
    snapLinesActive,
    showMaxSizeHint,
    snapLines,
  } = useConfigurationTransform(configuration, textElementRef, isSelected, false, minimumScale);

  const [onMoveOrEdit, onPinchOrEdit] = useMoveOrEdit(isSelected, onMove, onPinch, onShowEdit);

  const [textElement, transform] = useBendingText(configuration, offset, rotation, scale);

  return (
    <>
      <SnapLines viewMap={viewMap} lines={snapLines} />

      <g ref={textElementRef} transform={transform}>
        {textElement}
        <ConfigurationHandlesWithZoom
          active={isSelected}
          configuration={configuration}
          isSnapTarget={isSnapTarget}
          infoText={infoText}
          onDelete={onDelete}
          onMove={onMoveOrEdit}
          onPinch={onPinchOrEdit}
          onRotate={onRotate}
          onScale={onScale}
          scale={scale}
          viewMap={viewMap}
          onSetSnapLineActive={onSetSnapLineActive}
          snapLinesActive={snapLinesActive}
          hasConfigurationErrors={hasErrors}
        />
        <MaxSizeHint
          size={configuration.size}
          currentScale={scale}
          maxScale={maxScale}
          active={showMaxSizeHint}
        />
      </g>
    </>
  );
};
